import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/src/shadComponents/ui/dialog';

interface RenameDeviceKeyModalProps {
    deviceKey: DeviceKey;
    onDismiss: () => void;
    onRename: (keyId: string, newName: string) => Promise<void>;
}

export default function RenameDeviceKeyModal({
    deviceKey,
    onDismiss,
    onRename,
}: RenameDeviceKeyModalProps) {
    const [newName, setNewName] = useState(deviceKey.name);
    const [isLoading, setIsLoading] = useState(false);

    const handleRename = async () => {
        if (!newName.trim()) {
            return;
        }

        setIsLoading(true);
        try {
            await onRename(deviceKey.keyId, newName.trim());
            onDismiss();
        } catch (error) {
            console.error('Failed to rename device key:', error);
            // You might want to show an error message here
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleRename();
        }
    };

    return (
        <Dialog open={true} onOpenChange={() => onDismiss()}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary">
                            <Pencil
                                className="size-5 text-primary-foreground"
                                aria-hidden="true"
                            />
                        </div>
                        <div>
                            <DialogTitle>Rename Device Key</DialogTitle>
                            <DialogDescription>
                                Update the name for device key "
                                {deviceKey.name}".
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="name">Device Key Name</Label>
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onDismiss()}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRename}
                        disabled={isLoading || !newName.trim()}
                    >
                        {isLoading ? 'Renaming...' : 'Rename'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
